import { useEffect, useState, useRef } from 'react'
import { Label, Listbox, ListboxButton, ListboxOption, ListboxOptions } from '@headlessui/react'
import clsx from 'clsx'
import { IconSvg } from 'components/common'

interface Props {
	options: any[]
	defaultOption?: any
	onChange?: (...args: any) => any
	placeholder?: string
	value?: any
	disabled?: boolean
	className?: string
	itemFormatter?: (...args: any) => any
	dropdownClassName?: string
	dropdownAnchor?: boolean
	allowClear?: boolean
	allowNull?: boolean
}

export function SelectBox({
	options,
	defaultOption,
	onChange,
	placeholder = 'Choose option',
	value,
	disabled,
	className,
	itemFormatter,
	dropdownClassName,
	dropdownAnchor = true,
	allowClear = false,
	allowNull = false,
}: Props) {
	const [selected, setSelected] = useState<any>(defaultOption || null)
	const [buttonWidth, setButtonWidth] = useState<number>(0)
	const buttonRef = useRef<HTMLButtonElement>(null)

	useEffect(() => {
		if (value === undefined || (value === null && !allowNull)) {
			setSelected(null)
			return
		}
		const item = options.find((option: any) => option.value === value)
		setSelected(item || null)
	}, [value, options])

	// Update button width when component mounts or when content changes
	useEffect(() => {
		if (buttonRef.current) {
			setButtonWidth(buttonRef.current.offsetWidth)
		}
	}, [selected, options])

	const handleChange = (item: any) => {
		setSelected(item)
		onChange?.(item)
	}

	const handleClear = (e: React.MouseEvent) => {
		e.stopPropagation()
		setSelected(null)
		onChange?.(null)
	}

	return (
		<Listbox value={selected} onChange={handleChange} disabled={disabled}>
			<Label className="sr-only"> Choose your option </Label>
			<div className="relative w-full">
				<div className="relative">
					<ListboxButton
						ref={buttonRef}
						className={clsx(
							'px-4 rounded-lg transition-all duration-300 border border-translucent-plus-border bg-primary-surface/[0.4] backdrop-blur-lg text-primary-text h-10 flex justify-start items-center space-x-1 min-w-[248px] group w-full',
							className
						)}
					>
						{selected || (allowNull && selected?.value === null) ? (
							<>
								{itemFormatter ? (
									itemFormatter(selected)
								) : (
									<span className="block truncate">{selected.name}</span>
								)}
							</>
						) : (
							<span className="block text-secondary-text italic font-light">{placeholder}</span>
						)}
						<span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-4">
							{disabled ? (
								<IconSvg icon="lock" className="text-secondary-text" />
							) : (
								<IconSvg icon="dropdown" className="text-primary-icon" />
							)}
						</span>
					</ListboxButton>
					{selected && allowClear && !disabled && (
						<button
							onClick={handleClear}
							className="absolute right-10 top-1/2 -translate-y-1/2 flex items-center justify-center p-2 hover:bg-tint-plus-surface rounded-full transition-colors w-8 h-8 cursor-pointer"
							aria-label="Clear selection"
						>
							<IconSvg
								icon="close"
								className="w-4 h-4 text-secondary-text flex items-center justify-center"
							/>
						</button>
					)}
				</div>

				<ListboxOptions
					transition
					style={{ minWidth: buttonWidth > 0 ? `${buttonWidth}px` : 'auto' }}
					className={clsx(
						'absolute z-10 mt-2 max-h-60 overflow-auto rounded-lg bg-primary-surface/[0.4] backdrop-blur-lg p-2 text-base shadow-[0_16px_48px_0px_rgba(0,0,0,0.4)] ring-1 ring-primary-border ring-opacity-5 focus:outline-none data-[closed]:data-[leave]:opacity-0 data-[leave]:transition data-[leave]:duration-100 data-[leave]:ease-in backdrop-blur-[32px] space-y-1',
						dropdownClassName
					)}
					anchor={dropdownAnchor ? { to: 'bottom start', padding: '20px' } : undefined}
				>
					{options
						.filter(x => !x.hidden)
						.map((option: any, index: number) => (
							<ListboxOption
								key={index}
								className={({ selected, active }) =>
									clsx(
										'select-none px-4 py-2 text-secondary-text hover:bg-secondary-surface/[0.2] hover:backdrop-blur-lg hover:text-primary-text rounded-lg transition-all duration-300',
										{
											'!text-primary-text bg-secondary-surface/[0.2] backdrop-blur-lg':
												active || selected,
											'cursor-default': !option.disabled,
											'cursor-not-allowed opacity-50': option.disabled,
										}
									)
								}
								value={option}
								disabled={option.disabled}
							>
								{({ selected }) =>
									itemFormatter ? (
										itemFormatter(option, { selected })
									) : (
										<div className="flex flex-col">
											<div className="flex justify-between">
												<p
													className={clsx('font-normal', {
														'text-primary-text': selected,
													})}
												>
													{option.name}
												</p>
											</div>
										</div>
									)
								}
							</ListboxOption>
						))}
				</ListboxOptions>
			</div>
		</Listbox>
	)
}
